import { useRef, useEffect } from 'react';
import { polyfill } from 'mobile-drag-drop';
import { scrollBehaviourDragImageTranslateOverride } from 'mobile-drag-drop/scroll-behaviour';
import { FilterItem } from '../../../redux/reducers/StoreFilterReducer';
import { useDispatch } from 'react-redux';
import { storeFilterActions } from '../../../redux/modules/Actions';

export const dragStartConditionOverride = (event: TouchEvent) => {
  const target = event.target as HTMLElement | null;
  return !!target?.closest('ul.filter-modal__scroll-area');
};

export const useDragAndDrop = (items: FilterItem[]) => {
  const dispatch = useDispatch();
  const draggedItem = useRef<number | null>(null);
  const dragOverItem = useRef<number | null>(null);
  const usePolyfill = polyfill({
    // use this to make use of the scroll behaviour
    dragImageTranslateOverride: scrollBehaviourDragImageTranslateOverride,
    dragStartConditionOverride,
    holdToDrag: 500,
  });

  useEffect(() => {
    if (!usePolyfill) return;
    const onDragEnter = (event: DragEvent) => {
      event.preventDefault();
    };

    const onTouchMove = () => {
      // intentionally left blank
    };
    document.addEventListener('dragenter', onDragEnter);

    /**
     * If you're targeting iOS Safari 10.x and higher (Reference docs: mobile-drag-drop).
     * iOS>=10 supports passive event listeners
     * but make sure to catch or check passive event listener support
     * regarding this code running on other platforms.
     * window.addEventListener( 'touchmove', function() {}, {passive: false});
     */
    window.addEventListener('touchmove', onTouchMove, {
      passive: false,
    });
    return () => {
      document.removeEventListener('dragenter', onDragEnter);
      window.removeEventListener('touchmove', onTouchMove);
    };
  }, [usePolyfill]);

  const handleDragStart = (e: React.DragEvent<HTMLElement>, index: number) => {
    draggedItem.current = index;
    e.currentTarget.classList.add('dragging');
  };
  const handleDragEnter = (e: React.DragEvent<HTMLElement>, index: number) => {
    e.preventDefault();
    dragOverItem.current = index;
  };
  const handleDragOver = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
  };
  const handleDrageLeave = (e: React.DragEvent<HTMLElement>) => {
    e.currentTarget.classList.remove('drag-over');
  };
  const handleDragEnd = (e: React.DragEvent<HTMLElement>) => {
    e.currentTarget.classList.remove('dragging');
  };
  const handleDrop = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    if (draggedItem.current === null || dragOverItem.current === null) return;
    const updatedItems = [...items];
    const draggedItemContent = updatedItems[draggedItem.current];
    updatedItems.splice(draggedItem.current, 1);
    updatedItems.splice(dragOverItem.current, 0, draggedItemContent);

    dispatch(storeFilterActions.draftFilter.updateOrder(updatedItems));

    draggedItem.current = null;
    dragOverItem.current = null;
    e.currentTarget.classList.remove('drag-over');
  };

  return {
    handleDragStart,
    handleDragEnter,
    handleDragOver,
    handleDrageLeave,
    handleDragEnd,
    handleDrop,
  };
};
